import React, { useState, useMemo, useEffect } from 'react';
import { BonusGem, CostRow } from '../CardSection/Card';
import './PurchaseModal.css';

const GEM_COLORS = ['WHITE', 'BLUE', 'GREEN', 'RED', 'BLACK'];

/**
 * Compute the real cost of a card after applying bonuses.
 */
function computeRealCost(card, playerState) {
  const result = {};
  for (const [color, amount] of Object.entries(card.cost)) {
    const bonus = playerState.bonuses?.[color] || 0;
    const needed = Math.max(0, amount - bonus);
    if (needed > 0) result[color] = needed;
  }
  return result;
}

/**
 * Compute the default payment: use colored tokens first, gold covers the gap.
 */
function defaultPayment(realCost, playerState) {
  const pay = {};
  for (const [color, needed] of Object.entries(realCost)) {
    const have = playerState.tokens?.[color] || 0;
    pay[color] = Math.min(have, needed);
  }
  return pay;
}

/**
 * Check if there's any flexibility in how to pay (can swap color for gold).
 */
function hasPaymentChoice(realCost, playerState) {
  const gold = playerState.tokens?.GOLD || 0;
  if (gold === 0) return false;
  // If any color has tokens that could be replaced by gold, there's choice
  for (const [color, needed] of Object.entries(realCost)) {
    const have = playerState.tokens?.[color] || 0;
    if (Math.min(have, needed) > 0) return true; // could use gold instead
  }
  return false;
}

export default function PurchaseModal({ card, playerState, onConfirm, onCancel, busy }) {
  const [payment, setPayment] = useState({});
  const [phase, setPhase] = useState('choose'); // 'choose' | 'animating'

  const realCost = useMemo(() => computeRealCost(card, playerState), [card, playerState]);
  const canChoose = useMemo(() => hasPaymentChoice(realCost, playerState), [realCost, playerState]);

  // Initialize default payment
  useEffect(() => {
    setPayment(defaultPayment(realCost, playerState));
  }, [realCost, playerState]);

  // Gold used for each color
  const goldUsed = useMemo(() => {
    const g = {};
    for (const [color, needed] of Object.entries(realCost)) {
      g[color] = needed - (payment[color] || 0);
    }
    return g;
  }, [realCost, payment]);

  const totalGoldUsed = Object.values(goldUsed).reduce((s, v) => s + v, 0);
  const playerGold = playerState.tokens?.GOLD || 0;

  // Can player decrease colored tokens for this color? (shift to gold)
  const canDecColor = (color) => {
    const cur = payment[color] || 0;
    if (cur <= 0) return false;
    return totalGoldUsed + 1 <= playerGold; // enough gold left
  };

  // Can player increase colored tokens for this color? (shift away from gold)
  const canIncColor = (color) => {
    const cur = payment[color] || 0;
    const needed = realCost[color] || 0;
    if (cur >= needed) return false;
    const have = playerState.tokens?.[color] || 0;
    return cur < have;
  };

  const handleDec = (color) => {
    if (!canDecColor(color)) return;
    setPayment((p) => ({ ...p, [color]: (p[color] || 0) - 1 }));
  };

  const handleInc = (color) => {
    if (!canIncColor(color)) return;
    setPayment((p) => ({ ...p, [color]: (p[color] || 0) + 1 }));
  };

  const handleConfirm = () => {
    setPhase('animating');
    // Short animation delay then confirm
    setTimeout(() => {
      onConfirm(payment);
    }, 600);
  };

  const colorCls = card.color.toLowerCase();

  // Build list of all gems being spent (for animation)
  const spentGems = [];
  for (const [color, needed] of Object.entries(realCost)) {
    const colorPay = payment[color] || 0;
    const goldPay = needed - colorPay;
    for (let i = 0; i < colorPay; i++) spentGems.push(color);
    for (let i = 0; i < goldPay; i++) spentGems.push('GOLD');
  }

  return (
    <div className="modal-overlay">
      <div className={`purchase-modal ${phase === 'animating' ? 'purchase-modal--animating' : ''}`}>
        <h3>购买卡牌</h3>

        {/* Card preview */}
        <div className="purchase-modal__card-preview">
          <div className={`purchase-modal__card ${colorCls}`}>
            <div className={`purchase-modal__card-art ${colorCls}`}>
              <span className="sparkle" /><span className="sparkle" /><span className="sparkle" />
            </div>
            <div className="purchase-modal__card-info">
              <div className="purchase-modal__card-header">
                <span className="purchase-modal__card-pts">{card.points || 0}</span>
                <BonusGem color={card.color} size={22} />
              </div>
              <CostRow cost={card.cost} className="purchase-modal__card-cost" />
            </div>
          </div>
        </div>

        {/* Bonus discount display */}
        {Object.entries(card.cost).some(([c, amt]) => (playerState.bonuses?.[c] || 0) > 0 && amt > 0) && (
          <div className="purchase-modal__discount">
            <span className="purchase-modal__discount-label">宝石加成折扣：</span>
            {Object.entries(card.cost).map(([color, amt]) => {
              const bonus = playerState.bonuses?.[color] || 0;
              const disc = Math.min(bonus, amt);
              if (disc === 0) return null;
              return (
                <span key={color} className="purchase-modal__discount-item">
                  <span className={`gem-pip ${color.toLowerCase()}`} style={{ width: 16, height: 16, fontSize: 9 }}>
                    -{disc}
                  </span>
                </span>
              );
            })}
          </div>
        )}

        {/* Payment rows */}
        <div className="purchase-modal__payment">
          <div className="purchase-modal__payment-header">
            <span>实际花费</span>
            {playerGold > 0 && <span className="purchase-modal__gold-avail">金币剩余: {playerGold - totalGoldUsed}</span>}
          </div>

          {GEM_COLORS.map((color) => {
            const needed = realCost[color];
            if (!needed) return null;
            const colorPay = payment[color] || 0;
            const goldPay = needed - colorPay;

            return (
              <div key={color} className="purchase-modal__row">
                <span className={`gem-pip ${color.toLowerCase()}`} style={{ width: 28, height: 28, fontSize: 13 }}>
                  {needed}
                </span>
                <span className="purchase-modal__arrow">→</span>

                {/* Color token portion */}
                <div className="purchase-modal__token-group">
                  {canChoose && (
                    <button
                      className="purchase-modal__adj-btn"
                      onClick={() => handleDec(color)}
                      disabled={!canDecColor(color)}
                    >−</button>
                  )}
                  <span className={`gem-pip ${color.toLowerCase()} purchase-modal__gem-large`}>
                    {colorPay}
                  </span>
                  {canChoose && (
                    <button
                      className="purchase-modal__adj-btn"
                      onClick={() => handleInc(color)}
                      disabled={!canIncColor(color)}
                    >+</button>
                  )}
                </div>

                {/* Gold portion */}
                {goldPay > 0 && (
                  <>
                    <span className="purchase-modal__plus">+</span>
                    <span className="gem-pip gold purchase-modal__gem-large">{goldPay}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Animated gems (shown during animation phase) */}
        {phase === 'animating' && (
          <div className="purchase-modal__anim-layer">
            {spentGems.map((color, i) => (
              <span
                key={i}
                className={`purchase-modal__flying-gem gem-pip ${color.toLowerCase()}`}
                style={{ animationDelay: `${i * 80}ms` }}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="purchase-modal__actions">
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={busy || phase === 'animating'}
          >
            {phase === 'animating' ? '购买中...' : '确认购买'}
          </button>
          <button
            className="btn-secondary"
            onClick={onCancel}
            disabled={busy || phase === 'animating'}
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
